import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Anchor, Flex, Table, Text } from "@mantine/core";
import { AccessTimeOutlined, CakeOutlined, CalendarMonthOutlined, EditCalendarOutlined, EscalatorWarningOutlined, EventAvailable, GradeOutlined, InfoOutlined, InterestsOutlined, KeyboardArrowRightOutlined, LinkOutlined, LocationCityOutlined, LocationOnOutlined, MailOutline, Numbers, PhoneOutlined, Psychology, TranslateOutlined, VolunteerActivismOutlined } from "@mui/icons-material";
import { titleCase } from "../utils/String.utils";
import { DaysAvailable } from "./DaysAvailable";
import { EmailLink, PhoneLink } from "./ContactInfo";
import { MapLink } from "./MapLink";
// This is a mapping of keys to icons. These icons appear on the left of each row.
const iconMapping = {
    age: (_jsx(AccessTimeOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    availability: (_jsx(EventAvailable, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    caregiver: (_jsx(EscalatorWarningOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    created_at: (_jsx(CalendarMonthOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    inquired_at: (_jsx(CalendarMonthOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    created_date: (_jsx(CalendarMonthOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    date_of_birth: (_jsx(CakeOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    preferred_languages: (_jsx(TranslateOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    grade_level: (_jsx(GradeOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    email: (_jsx(MailOutline, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    joined: (_jsx(CalendarMonthOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    location: (_jsx(LocationOnOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    modality: (_jsx(InterestsOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    NPI: (_jsx(Numbers, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    organization: (_jsx(LocationCityOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    organization_id: (_jsx(Numbers, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    phone: (_jsx(PhoneOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    providers: (_jsx(Psychology, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    status: (_jsx(KeyboardArrowRightOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    website: (_jsx(LinkOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    therapy: (_jsx(VolunteerActivismOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    therapy_needed: (_jsx(VolunteerActivismOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-4)" } })),
    type: (_jsx(InfoOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-6)" } })),
    updated_at: (_jsx(EditCalendarOutlined, { style: { fontSize: "1rem", color: "var(--mantine-color-gray-6)" } }))
};
// This is a mapping of keys to rows. Each row potentially has different actions (copying a value, opening the email client, etc...)
const rowMapping = ({ key, props }) => {
    if (!props[key])
        return null;
    switch (key) {
        case "website":
            return (_jsx(Table.Td, { style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }, children: _jsx(Anchor, { className: "underline", href: String(props[key]).startsWith("http")
                        ? String(props[key])
                        : `https://${String(props[key])}`, target: "_blank", rel: "noopener noreferrer", children: String(props[key]) }) }));
        case "email":
            return (_jsx(Table.Td, { style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }, children: _jsx(EmailLink, { email: String(props[key]) }) }));
        case "phone":
            return (_jsx(Table.Td, { style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }, children: _jsx(PhoneLink, { phoneNumber: String(props[key]) }) }));
        case "location":
            return (_jsx(Table.Td, { children: _jsx(MapLink, { address: String(props[key]), children: String(props[key]) }) }));
        case "availability":
            return (_jsx(Table.Td, { children: typeof props[key] === "object" &&
                    props[key] !== null &&
                    "days" in props[key] &&
                    "times" in props[key] && (_jsx(DaysAvailable, { daysAvailable: props[key]?.days, timesAvailable: props[key]?.times })) }));
        default:
            return (_jsx(Table.Td, { children: typeof props[key] === "string" ? (_jsx(Text, { variant: "body", fz: 12, fw: 500, children: props[key] })) : typeof props[key] === "object" &&
                    props[key] !== null ? props[key] : (_jsx(Text, { variant: "body", fz: 12, fw: 500, children: String(props[key]) })) }));
    }
};
export const KeyValueTable = (props) => {
    // Create table rows from the props
    const rows = Object.keys(props).map((key) => {
        // If the key is all uppercase, leave it that way.
        // Else, title case it.
        const keyTitle = key.toUpperCase() === key ? key : titleCase(key.replace(/_/g, " "));
        return (_jsxs("tr", { style: {
                display: "flex",
                justifyContent: "space-between",
                padding: "0.5rem",
                gap: "1rem",
                overflow: "hidden",
                borderBottom: "1px solid lightgray"
            }, children: [_jsx(Table.Td, { style: { alignContent: "center" }, children: _jsxs(Flex, { align: "center", gap: "xs", children: [iconMapping[key], _jsx(Text, { variant: "meta", fw: 600, children: keyTitle })] }) }), rowMapping({ key, props })] }, key));
    });
    return (_jsx(Table, { style: {
            display: "flex",
            width: "100%",
            borderRadius: "0.65rem",
            boxShadow: "0 0 10px color-mix(in srgb, var(--mantine-color-black) 10%, transparent)",
            backgroundColor: "white"
        }, children: _jsx(Table.Tbody, { style: { flex: 1 }, children: rows }) }));
};
